import * as React from "react";

import debounce from "lodash/debounce";
import PubSub from "pubsub-js";
import { TITLE_BAR_TAP, COMPONENTS_CHANGED, SCROLL_CHANGED } from "../../../lib/constants";
import {
    ScrollWrapperStyle,
    ScrollRegionStyle,
    ScrollContentStyle,
    ScrollOverlayDiv,
    ScrollOverlayContent,
} from "./scroll-view-style";
import { ScrollOverlayProvider } from "./scroll-view-overlay";
import type { RenderEnvironment } from "common/components/types";
import type { RouteComponentProps} from "react-router-dom";
import { withRouter } from "react-router-dom";
import { massageCloudURL } from "common/components/portable-renderers";
import { extractDropContents } from "sharedUI/lib/drop-utils";
import { handleFileUpload } from "sharedUI/lib/upload-utils";
import classnames from "classnames";
import { GLIDE_COMPONENT_INDEX_DATA_FORMAT, GLIDE_TABLE_COLUMN_DATA_FORMAT } from "sharedUI/lib/constants";

interface Props extends RouteComponentProps<{ page: string }> {
    readonly tabs?: boolean;
    readonly topPadding: number;
    readonly id: string;
    readonly renderEnv: RenderEnvironment;
}

interface State {
    readonly dragOver: boolean;
}

class ScrollView extends React.PureComponent<Props, State> {
    public state = {
        dragOver: false,
    };

    private scroller: HTMLDivElement | null = null;
    private scrollTopToken: any;
    private scrollBottomToken: any;

    private onScrollChange = (scroller: HTMLDivElement) => {
        if (this.scroller === scroller) return;

        if (this.scroller !== null) {
            this.removeScrollListener();
        }

        this.scroller = scroller;

        if (this.scroller !== null) {
            this.addScrollListener();
        }
    };

    // Dear friends, allow me to explain this hack. Basically when overscroll-behavior is not
    // supported by our target browser, if the user scrolls to an extreme of the page scrollview
    // and then tried to scroll further, the main document scroller will get the events and to the
    // user it will appear to be swallowed. This is annoying.

    // So we ensure the user is always 1px off from the end, and then just use some CSS transforms to
    // make it look like they are not. This is effective especially on mobile devices where 1px is
    // super tiny. This hack does have artifacts however, so we turn it off when possible.

    private needsOverscrollHack(): boolean {
        return !CSS.supports("overscroll-behavior: contain");
    }

    // God bless that A12 chip, we are going to work it extra hard just because there is a tiny flicker.
    // The react-virtualized lib doesn't really handle overscroll at the bottom that well, fortuantely we can
    // just not, ya know, tell it about that happening. Every bad thing this method dies it kind of already
    // does so we aren't really taking a big perf hit. Still this is some pretty uniquely dumb shit.
    private onScrollWrapper = (ev: Event) => {
        const scroller = this.scroller;
        if (scroller === null) return;
        const current = scroller.scrollTop;

        const needsHack = this.needsOverscrollHack();

        PubSub.publishSync(SCROLL_CHANGED, {
            position: needsHack ? Math.max(0, current - 1) : current,
            id: this.props.id,
        });

        if (!needsHack) return;
        const scrollHeight = scroller.scrollHeight;
        const clientHeight = scroller.clientHeight;

        if (current > scrollHeight - clientHeight) {
            ev.stopImmediatePropagation();
        }

        this.onScroll(current, scrollHeight, clientHeight);
    };

    // You might think there is a less dumb way to define this
    private onScroll = debounce<(current: number, scrollHeight: number, clientHeight: number) => void>(
        (current: number, scrollHeight: number, clientHeight: number) => {
            const scroller = this.scroller;
            if (scroller === null) return;

            const bottom = scrollHeight - clientHeight;

            // because of the debounce we need to double check that we are doing the right thing.
            if (current === 0 || (current === bottom && bottom > 1)) {
                current = scroller.scrollTop;
            }

            if (current === 0) {
                scroller.scrollTop = 1;
            } else if (current === bottom && bottom > 1) {
                scroller.scrollTop = scrollHeight - clientHeight - 1;
            }
        },
        100
    );

    private addScrollListener(): void {
        const scroller = this.scroller;
        if (scroller === null) return;
        scroller.addEventListener("scroll", this.onScrollWrapper, { passive: false });

        if (!this.needsOverscrollHack()) return;
        // Okay this is weird code. iOS forces us to do a funky indent logic here to hack around its
        // lack of overscroll behavior. Unfortunately we dont know if we need to scroll or not until we
        // have actually been laid out, to which I do not believe there is a great event. Soo we
        // just give an optimistic and pesimistic timer, and then hope for the best. Honestly if neither
        // catch it the issue will clear its up the first time they scroll so not a big deal.
        setTimeout(() => this.onScroll(scroller.scrollTop, scroller.scrollHeight, scroller.clientHeight), 200);
        this.onScroll(scroller.scrollTop, scroller.scrollHeight, scroller.clientHeight);
    }

    private removeScrollListener(): void {
        const scroller = this.scroller;
        if (scroller === null) return;
        scroller.removeEventListener("scroll", this.onScrollWrapper);
    }

    private onScrollTop = (_msg: string, _data: any): void => {
        const scroller = this.scroller;
        if (scroller !== null) {
            scroller.scroll({ top: 0, behavior: "smooth" });
        }
    };

    private onScrollBottom = (_msg: string, data: any): void => {
        const scroller = this.scroller;
        if (scroller !== null) {
            scroller.scroll({ top: scroller.scrollHeight, behavior: data });
        }
    };

    public componentDidMount(): void {
        this.scrollTopToken = PubSub.subscribe(TITLE_BAR_TAP, this.onScrollTop);
        this.scrollBottomToken = PubSub.subscribe(COMPONENTS_CHANGED, this.onScrollBottom);
    }

    public componentWillUnmount(): void {
        PubSub.unsubscribe(this.scrollTopToken);
        PubSub.unsubscribe(this.scrollBottomToken);
        this.removeScrollListener();
    }

    private onImageDropped = (image: string) => {
        const { renderEnv } = this.props;
        renderEnv.appEnvironment.addImageComponent(undefined, undefined, image);
    };

    private onDragOver = (ev: React.DragEvent<HTMLElement>) => {
        if (
            !this.props.renderEnv.appEnvironment.isBuilder ||
            ev.dataTransfer.types.includes(GLIDE_COMPONENT_INDEX_DATA_FORMAT)
        )
            return;

        ev.stopPropagation();
        ev.preventDefault();

        this.setState({
            dragOver: true,
        });
    };

    private onDragLeave = () => {
        if (!this.props.renderEnv.appEnvironment.isBuilder) return;

        this.setState({
            dragOver: false,
        });
    };

    private async onFileDropped(file: File): Promise<void> {
        const url = await handleFileUpload(file, this.props.renderEnv.appEnvironment.appID);
        if (url !== undefined) {
            this.onImageDropped(url);
        }
    }

    private onURIDropped(uri: string): void {
        uri = massageCloudURL(uri);
        this.onImageDropped(uri);
    }

    private onColumnDropped = (columnName: string) => {
        const { renderEnv } = this.props;
        renderEnv.appEnvironment.addComponentForColumn(undefined, undefined, columnName);
    };

    private onDrop = async (ev: React.DragEvent<HTMLElement>) => {
        if (!this.props.renderEnv.appEnvironment.isBuilder) return;

        this.setState({ dragOver: false });

        try {
            if (ev.dataTransfer.types.includes(GLIDE_TABLE_COLUMN_DATA_FORMAT)) {
                ev.stopPropagation();
                ev.preventDefault();

                this.onColumnDropped(ev.dataTransfer.getData(GLIDE_TABLE_COLUMN_DATA_FORMAT));
            } else {
                const dropContents = await extractDropContents(ev);
                if (dropContents === undefined) return;

                this.props.renderEnv.appEnvironment.startBuilderBusy();

                ev.stopPropagation();
                ev.preventDefault();

                if (dropContents.kind === "file") {
                    await this.onFileDropped(dropContents.value[0]);
                } else {
                    await this.onURIDropped(dropContents.value);
                }
            }
        } finally {
            this.props.renderEnv.appEnvironment.stopBuilderBusy();
        }
    };

    public render() {
        const { topPadding, tabs, id, children, renderEnv } = this.props;

        const needsOverscrollHack = this.needsOverscrollHack();
        const extraPadding = needsOverscrollHack ? 1 : 0;
        const overlayId = "Overlay" + id;
        const isTablet = renderEnv.sizeClass === "tablet";
        const inTabletDetail = isTablet && renderEnv.displayContext === "detail";
        const inTabletModal = isTablet && renderEnv.displayContext === "modal";

        return (
            <ScrollWrapperStyle
                className={classnames(this.state.dragOver && "drag-over", this.props.renderEnv.displayContext)}
                onDragOver={this.onDragOver}
                onDragLeave={this.onDragLeave}
                onDrop={this.onDrop}
                inTabletDetail={inTabletDetail}
                inTabletModal={inTabletModal}>
                <ScrollOverlayDiv extraPadding={extraPadding} tabs={tabs} topPadding={topPadding}>
                    <ScrollOverlayContent id={overlayId}>
                        <div id={overlayId + "fab-target"} className="fab-target" />
                    </ScrollOverlayContent>
                </ScrollOverlayDiv>
                <ScrollRegionStyle
                    ref={this.onScrollChange}
                    topPadding={topPadding}
                    id={id}
                    tabs={tabs}
                    extraPadding={0}>
                    <ScrollContentStyle
                        extraPadding={extraPadding}
                        tabs={tabs}
                        inTabletDetail={inTabletDetail}
                        className={this.props.match.params.page === "tabs" ? "" : "focused"}>
                        <ScrollOverlayProvider value={overlayId}>{children}</ScrollOverlayProvider>
                    </ScrollContentStyle>
                </ScrollRegionStyle>
            </ScrollWrapperStyle>
        );
    }
}

export default withRouter(ScrollView);
